import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, Plus, Minus, X, Check, Flame } from 'lucide-react';
import { useToast, Input, Button, FilterTabs } from './ui';
import { fmtMoney } from '../utils/currency';

// The complete order-punching surface: menu grid with search + category
// filter + stock guards, customer linking, and a cart with quantity, spice
// level, add-ons and per-item notes. Shared by the POS (cashier station,
// which adds payment on top) and the Orders page (waiter order entry, no
// payment) so the two can never drift apart.
//
// Props:
//   onPlaced(order, orderType) — called after a successful place; the cart
//     resets itself first.
//   payFlow — true on the POS: the button reads "Place Order & Pay"
//     (payments are handled by the parent); false = plain "Place Order".

const ORDER_TYPES = [
  { value: 'dine-in',    label: 'Dine-in' },
  { value: 'takeaway',   label: 'Takeaway' },
  { value: 'delivery',   label: 'Delivery' },
  { value: 'drive-thru', label: 'Drive-thru' },
];

// Spice level options surfaced on the cart line. `''` = no preference (default).
const SPICE_OPTIONS = [
  { value: '',          label: 'No spice' },
  { value: 'mild',      label: 'Mild' },
  { value: 'medium',    label: 'Medium' },
  { value: 'hot',       label: 'Hot' },
  { value: 'extra_hot', label: 'Extra hot' },
];

const OrderBuilder = ({ onPlaced, payFlow = false }) => {
  const { toast } = useToast();
  const [menuItems, setMenuItems] = useState([]);
  const [inventoryMap, setInventoryMap] = useState({}); // { invId: { quantity, unit, name } }
  const [orderItems, setOrderItems] = useState([]);
  const [orderType, setOrderType] = useState('dine-in');
  const [tableNumber, setTableNumber] = useState('');
  const [placing, setPlacing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [menuSearch, setMenuSearch] = useState('');
  const [category, setCategory] = useState('all');

  // Customer linking
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    fetchMenu();
    fetchInventory();
  }, []);

  const fetchMenu = async () => {
    try {
      const res = await axios.get('/api/menu', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setMenuItems(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchInventory = async () => {
    try {
      const res = await axios.get('/api/inventory?limit=500', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      const map = {};
      (res.data || []).forEach(i => {
        map[i._id] = { quantity: i.quantity, unit: i.unit, name: i.name };
      });
      setInventoryMap(map);
    } catch (err) {
      // Non-fatal — out-of-stock indicators just won't show
      setInventoryMap({});
    }
  };

  // Returns null if available; otherwise { reason, name } describing what's short.
  const stockShortage = (menuItem) => {
    if (!menuItem.recipe || menuItem.recipe.length === 0) return null;
    for (const ing of menuItem.recipe) {
      const invId = ing.inventoryItem?._id || ing.inventoryItem;
      const inv = inventoryMap[invId];
      // If we can't find the inventory record (e.g. no read access), don't gate the UI
      if (!inv) continue;
      if ((inv.quantity ?? 0) < ing.quantity) {
        return { name: inv.name, available: inv.quantity, needed: ing.quantity, unit: ing.unit };
      }
    }
    return null;
  };

  // Debounced customer search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!customerSearch || selectedCustomer) {
      setCustomerResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/customers?search=${encodeURIComponent(customerSearch)}`, {
          headers: { 'x-auth-token': localStorage.getItem('token') }
        });
        setCustomerResults((res.data || []).slice(0, 5));
      } catch (err) {
        setCustomerResults([]);
      }
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [customerSearch, selectedCustomer]);

  const addToOrder = (item) => {
    if (item.isAvailable === false) return;
    if (stockShortage(item)) {
      const shortage = stockShortage(item);
      toast.warning(`Out of stock: ${shortage.name} (need ${shortage.needed} ${shortage.unit}, have ${shortage.available})`);
      return;
    }
    const existing = orderItems.find(i => i.menuItem === item._id);
    if (existing) {
      setOrderItems(orderItems.map(i =>
        i.menuItem === item._id ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setOrderItems([...orderItems, {
        menuItem: item._id,
        name: item.name,
        price: item.price,
        quantity: 1,
        spiceLevel: '',
        notes: '',
        availableAddOns: item.addOns || [], // snapshot of options at add time
        selectedAddOns: [],
      }]);
    }
  };

  const setSpice = (id, value) => {
    setOrderItems(prev => prev.map(i => i.menuItem === id ? { ...i, spiceLevel: value } : i));
  };

  const setNotes = (id, value) => {
    setOrderItems(prev => prev.map(i => i.menuItem === id ? { ...i, notes: value } : i));
  };

  const toggleAddOn = (id, addOnName) => {
    setOrderItems(prev => prev.map(i => {
      if (i.menuItem !== id) return i;
      const has = (i.selectedAddOns || []).some(a => a.name === addOnName);
      if (has) {
        return { ...i, selectedAddOns: i.selectedAddOns.filter(a => a.name !== addOnName) };
      }
      const def = (i.availableAddOns || []).find(a => a.name === addOnName);
      if (!def) return i;
      return { ...i, selectedAddOns: [...(i.selectedAddOns || []), { name: def.name, price: Number(def.price) || 0 }] };
    }));
  };

  const changeQty = (id, delta) => {
    setOrderItems(prev =>
      prev
        .map(i => i.menuItem === id ? { ...i, quantity: i.quantity + delta } : i)
        .filter(i => i.quantity > 0)
    );
  };

  const removeFromOrder = (id) => setOrderItems(orderItems.filter(i => i.menuItem !== id));

  // Per-unit add-on cost for a cart line
  const addOnsPerUnit = (item) => (item.selectedAddOns || []).reduce((s, a) => s + (Number(a.price) || 0), 0);
  // Full line total (base + add-ons) × quantity
  const lineTotal = (item) => (item.price + addOnsPerUnit(item)) * item.quantity;

  const total = orderItems.reduce((sum, i) => sum + lineTotal(i), 0);

  // Category tabs derived from the menu itself
  const categories = ['all', ...Array.from(new Set(menuItems.map(i => i.category || 'Other')))];

  // Filter the menu grid by category tab + search box (name or category)
  const query = menuSearch.trim().toLowerCase();
  const filteredMenuItems = menuItems.filter(item => {
    if (category !== 'all' && (item.category || 'Other') !== category) return false;
    if (!query) return true;
    return (item.name || '').toLowerCase().includes(query) ||
      (item.category || '').toLowerCase().includes(query);
  });

  const resetCart = () => {
    setOrderItems([]);
    setTableNumber('');
    setCustomerSearch('');
    setSelectedCustomer(null);
    setCustomerResults([]);
    setErrorMsg('');
  };

  const placeOrder = async () => {
    if (orderItems.length === 0) return;
    if (orderType === 'dine-in' && !tableNumber) {
      setErrorMsg('Please enter a table number for dine-in orders.');
      return;
    }
    setPlacing(true);
    setErrorMsg('');
    try {
      const payload = {
        items: orderItems.map(({ menuItem, quantity, notes, spiceLevel, selectedAddOns }) => ({
          menuItem,
          quantity,
          notes: notes || undefined,
          spiceLevel: spiceLevel || null,
          // Send just names; backend validates against menu and uses server-side prices
          selectedAddOns: (selectedAddOns || []).map(a => ({ name: a.name })),
        })),
        orderType,
        tableNumber: orderType === 'dine-in' ? parseInt(tableNumber, 10) : undefined,
        customer: selectedCustomer?._id
      };
      const res = await axios.post('/api/orders', payload, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      const newOrder = res.data;
      const placedType = orderType;
      const placedCustomer = selectedCustomer;
      resetCart();
      if (onPlaced) {
        onPlaced({ ...newOrder, customer: placedCustomer || newOrder.customer }, placedType);
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.msg || err.message || 'Order failed');
    } finally {
      setPlacing(false);
    }
  };

  return (
    <>
      {errorMsg && (
        <div className="mb-4 p-3 rounded-md bg-danger-50 border border-danger-100 text-danger-700 text-sm">
          {errorMsg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Left: Order-type tabs + menu grid */}
        <div className="lg:col-span-2 space-y-4 min-w-0">
          <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-4 flex flex-wrap items-center gap-3">
            <FilterTabs
              value={orderType}
              onChange={setOrderType}
              options={ORDER_TYPES}
            />
            <div className="w-full sm:w-56 sm:ml-auto order-last sm:order-none">
              <Input
                type="text"
                placeholder="Search menu…"
                value={menuSearch}
                onChange={(e) => setMenuSearch(e.target.value)}
                leftIcon={<Search size={14} />}
                rightIcon={menuSearch ? (
                  <button
                    type="button"
                    onClick={() => setMenuSearch('')}
                    className="text-neutral-400 hover:text-neutral-700 transition-colors"
                    title="Clear search"
                  >
                    <X size={14} />
                  </button>
                ) : undefined}
              />
            </div>
            {orderType === 'dine-in' && (
              <div className="w-full sm:w-32">
                <Input
                  type="number"
                  placeholder="Table #"
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Category filter — derived from the menu's own categories */}
          {categories.length > 2 && (
            <div className="flex flex-wrap gap-1.5">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors capitalize
                    ${category === cat
                      ? 'bg-neutral-900 text-white border-neutral-900'
                      : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'}`}
                >
                  {cat === 'all' ? 'All' : cat}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredMenuItems.map(item => {
              const unavailable = item.isAvailable === false;
              const shortage = unavailable ? null : stockShortage(item);
              const blocked = unavailable || !!shortage;
              return (
                <button
                  key={item._id}
                  onClick={() => addToOrder(item)}
                  disabled={blocked}
                  className={`
                    group text-left bg-white rounded-lg border border-neutral-200 shadow-card p-4
                    transition-all relative
                    ${blocked
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:shadow-card-hover hover:border-primary-300 cursor-pointer active:scale-[0.98]'}
                  `}
                  title={shortage ? `${shortage.name}: have ${shortage.available} ${shortage.unit}, need ${shortage.needed}` : undefined}
                >
                  <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">
                    {item.category || 'Other'}
                  </p>
                  <h3 className="font-semibold text-sm text-neutral-900 mb-2 leading-tight">{item.name}</h3>
                  <p className="text-base font-bold text-primary-600 tabular-nums">{fmtMoney(item.price)}</p>
                  {unavailable && (
                    <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-neutral-200 text-neutral-700 rounded">
                      Unavailable
                    </span>
                  )}
                  {!unavailable && shortage && (
                    <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-danger-100 text-danger-700 rounded">
                      Out of stock
                    </span>
                  )}
                </button>
              );
            })}
            {filteredMenuItems.length === 0 && (
              <div className="col-span-4 bg-white rounded-lg border border-neutral-200 p-8 text-center text-sm text-neutral-500">
                {menuItems.length === 0
                  ? 'No menu items. Add items from the Menu tab to start taking orders.'
                  : 'No items match the current search/category.'}
              </div>
            )}
          </div>
        </div>

        {/* Right: cart panel */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden flex flex-col">
          {/* Customer linking */}
          <div className="px-5 py-4 border-b border-neutral-200">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-neutral-800">Customer</label>
              <span className="text-[11px] text-neutral-500">Optional · for loyalty</span>
            </div>
            {selectedCustomer ? (
              <div className="p-3 bg-success-50 border border-success-200 rounded-md">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-neutral-900 flex items-center gap-1.5">
                      <Check size={14} className="text-success-600 flex-shrink-0" />
                      {selectedCustomer.name}
                    </p>
                    <p className="text-xs text-neutral-600 mt-1 truncate">{selectedCustomer.phone}</p>
                    <p className="text-xs text-neutral-600 mt-0.5">
                      <span className="font-semibold tabular-nums">{selectedCustomer.loyaltyPoints || 0}</span> pts
                      {' · '}
                      <span className="capitalize">{selectedCustomer.loyaltyTier || 'bronze'}</span>
                      {' · '}
                      <span className="tabular-nums">{fmtMoney(selectedCustomer.totalSpent || 0)}</span> spent
                    </p>
                  </div>
                  <button
                    onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }}
                    className="text-neutral-400 hover:text-danger-600 transition-colors flex-shrink-0"
                    title="Unlink"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search name or phone…"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  leftIcon={<Search size={14} />}
                />
                {customerSearch && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-neutral-200 rounded-md shadow-card-hover z-20 max-h-64 overflow-y-auto mt-1">
                    {customerResults.length > 0 ? (
                      customerResults.map(c => (
                        <button
                          key={c._id}
                          onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); setCustomerResults([]); }}
                          className="w-full text-left p-3 hover:bg-neutral-50 text-sm border-b border-neutral-100 last:border-b-0"
                        >
                          <p className="font-semibold text-neutral-900">{c.name}</p>
                          <p className="text-xs text-neutral-500 mt-0.5">
                            {c.phone} · <span className="tabular-nums">{c.loyaltyPoints || 0}</span> pts · <span className="capitalize">{c.loyaltyTier || 'bronze'}</span>
                          </p>
                        </button>
                      ))
                    ) : (
                      <div className="p-3 text-sm text-neutral-500 italic">
                        No matches. Leaving blank = walk-in order.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Cart items */}
          <div className="px-5 py-4 flex-1 overflow-y-auto">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm font-semibold text-neutral-800 uppercase tracking-wider">Current Order</h2>
              <span className="text-xs text-neutral-500">{orderItems.length} item{orderItems.length === 1 ? '' : 's'}</span>
            </div>

            {orderItems.length === 0 && (
              <div className="py-8 text-center">
                <p className="text-sm text-neutral-500">Cart is empty</p>
                <p className="text-xs text-neutral-400 mt-1">Tap menu items to add them</p>
              </div>
            )}

            <ul className="space-y-3">
              {orderItems.map(item => (
                <li key={item.menuItem} className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">{item.name}</p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <button
                        onClick={() => changeQty(item.menuItem, -1)}
                        className="w-7 h-7 rounded-md border border-neutral-200 bg-white hover:bg-neutral-100 text-neutral-700 flex items-center justify-center transition-colors"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-sm font-medium w-7 text-center tabular-nums">{item.quantity}</span>
                      <button
                        onClick={() => changeQty(item.menuItem, 1)}
                        className="w-7 h-7 rounded-md border border-neutral-200 bg-white hover:bg-neutral-100 text-neutral-700 flex items-center justify-center transition-colors"
                      >
                        <Plus size={12} />
                      </button>
                      <button
                        onClick={() => removeFromOrder(item.menuItem)}
                        className="ml-2 text-[11px] text-neutral-400 hover:text-danger-600 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                    {/* Spice picker */}
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Flame size={12} className={
                        item.spiceLevel === 'extra_hot' ? 'text-danger-600' :
                        item.spiceLevel === 'hot' ? 'text-danger-500' :
                        item.spiceLevel === 'medium' ? 'text-warning-500' :
                        item.spiceLevel === 'mild' ? 'text-success-500' :
                        'text-neutral-300'
                      } />
                      <select
                        value={item.spiceLevel || ''}
                        onChange={(e) => setSpice(item.menuItem, e.target.value)}
                        className="text-[11px] border border-neutral-200 rounded bg-white px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500"
                      >
                        {SPICE_OPTIONS.map(opt => (
                          <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Add-ons (only shown if this menu item has any defined) */}
                    {(item.availableAddOns || []).length > 0 && (
                      <div className="mt-1.5 space-y-0.5">
                        <p className="text-[10px] uppercase tracking-wider text-neutral-500">Add-ons</p>
                        {(item.availableAddOns || []).map(addOn => {
                          const isOn = (item.selectedAddOns || []).some(a => a.name === addOn.name);
                          return (
                            <label
                              key={addOn.name}
                              className="flex items-center gap-1.5 text-[11px] cursor-pointer hover:bg-neutral-50 px-1 py-0.5 rounded"
                            >
                              <input
                                type="checkbox"
                                checked={isOn}
                                onChange={() => toggleAddOn(item.menuItem, addOn.name)}
                                className="w-3 h-3 rounded border-neutral-300 text-primary-600 focus:ring-2 focus:ring-primary-200"
                              />
                              <span className="flex-1 text-neutral-700">{addOn.name}</span>
                              <span className="text-neutral-500 tabular-nums">+{fmtMoney(Number(addOn.price) || 0, true)}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {/* Kitchen notes ("no onions", "well done", ...) */}
                    <input
                      type="text"
                      value={item.notes || ''}
                      onChange={(e) => setNotes(item.menuItem, e.target.value)}
                      placeholder="Note for kitchen…"
                      maxLength={120}
                      className="mt-1.5 w-full text-[11px] border border-neutral-200 rounded bg-white px-1.5 py-1 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500"
                    />
                  </div>
                  <p className="text-sm font-semibold text-neutral-900 tabular-nums whitespace-nowrap">
                    {fmtMoney(lineTotal(item), true)}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          {/* Total + action */}
          <div className="px-5 py-4 border-t border-neutral-200 bg-neutral-50">
            <div className="flex justify-between items-baseline mb-3">
              <span className="text-sm font-medium text-neutral-600">Total</span>
              <span className="text-2xl font-bold text-neutral-900 tabular-nums">{fmtMoney(total, true)}</span>
            </div>
            <Button
              variant={orderType === 'delivery' || !payFlow ? 'primary' : 'success'}
              size="lg"
              fullWidth
              disabled={orderItems.length === 0}
              loading={placing}
              onClick={placeOrder}
            >
              {payFlow
                ? (orderType === 'delivery' ? 'Send to Kitchen' : 'Place Order & Pay')
                : 'Place Order'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderBuilder;
